import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { ConfirmDialogConfig } from '@/stores/uiStore';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  config: ConfirmDialogConfig | null;
  pending?: boolean;
}

const variantConfig = {
  danger: {
    icon: AlertTriangle,
    iconColor: 'text-rose-200',
    bgColor: 'bg-rose-500/15 border border-rose-500/30',
    buttonColor: 'bg-rose-500 hover:bg-rose-400 focus:ring-rose-400',
  },
  warning: {
    icon: AlertCircle,
    iconColor: 'text-amber-200',
    bgColor: 'bg-amber-500/15 border border-amber-500/30',
    buttonColor: 'bg-amber-500 hover:bg-amber-400 focus:ring-amber-400',
  },
  info: {
    icon: Info,
    iconColor: 'text-violet-200',
    bgColor: 'bg-violet-500/15 border border-violet-500/30',
    buttonColor: 'bg-violet-500 hover:bg-violet-400 focus:ring-violet-400',
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ isOpen, onClose, config, pending = false }) => {
  if (!config) return null;

  const variant = variantConfig[config.variant || 'info'];
  const Icon = variant.icon;

  const handleConfirm = () => {
    config.onConfirm();
    onClose();
  };

  const handleCancel = () => {
    config.onCancel?.();
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={handleCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-3 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl mm-glass p-4 sm:p-6 text-left align-middle shadow-2xl transition-all safe-area-bottom"
                role="alertdialog"
                aria-labelledby="dialog-title"
                aria-describedby="dialog-description"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`flex-shrink-0 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 ${variant.bgColor}`} aria-hidden="true">
                    <Icon className={variant.iconColor} size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Dialog.Title as="h2" id="dialog-title" className="text-base sm:text-lg font-semibold text-white">
                      {config.title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p id="dialog-description" className="text-sm text-white/70 leading-relaxed">{config.message}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 modal-actions">
                  <button
                    type="button"
                    className="mm-btn mm-btn-secondary btn-mobile focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-transparent rounded-xl touch-target"
                    onClick={handleCancel}
                  >
                    {config.cancelText || 'Cancel'}
                  </button>
                  <button
                    type="button"
                    className={`mm-btn btn-mobile text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent rounded-xl touch-target disabled:opacity-50 disabled:cursor-not-allowed ${variant.buttonColor}`}
                    onClick={handleConfirm}
                    disabled={pending}
                    autoFocus
                  >
                    {config.confirmText || 'Confirm'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
